import React, { Fragment } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { BrowserRouter, Route, withRouter } from 'react-router-dom';
import Select from 'react-select';
import invert from 'lodash/invert';
import pickBy from 'lodash/pickBy';
import isEqual from 'lodash/isEqual';

import APIClient, { iterUnpack } from '../services/APIClient';
import {
  arrayToReadableString,
  prepareOptionAsGqlParameter,
  prepareOptionsAsGqlParameter,
  prepareOption,
  prepareOptions,
  numberWithCommas,
  architizerHistoryListener,
} from '../services/utils';
import SegmentClient from '../services/SegmentClient';
import {
  BASE_URL,
  POPULAR_IMAGES,
  DETAIL_CATEGORY,
  OPERATION,
  CATEGORIES,
  ANALYTICS_NAME,
  filtersMap,
  emptyFilterMessage,
  URL_TYPE,
  SORTINGS,
  SORTING_KEY,
  BASE_URL_PATTERN,
} from '../components/image_search/Constants';
import FilterPill from '../components/image_search/FilterPill';
import ImageCard from '../components/image_search/ImageCard';
import { UserResource } from '../services/RESTClient';
import RequireRegisterPopup, { PERMISSION_TYPES } from '../components/RequireRegisterPopup';
import {
  PAGES_TITLES,
  MEDIA_QUERIES,
} from '../services/constants';
import GA4Client from '../services/GA4Client';

const ga4 = new GA4Client();

const userResource = new UserResource();

const getRandomSeed = (randomSeedsSet) => (randomSeedsSet.length
  ? randomSeedsSet[Math.floor(Math.random() * 10000) % randomSeedsSet.length]
  : null);

const ROWS_PER_LOAD = 5;

const COLUMNS_PER_SCREEN_LARGE = 4;
const COLUMNS_PER_SCREEN_MEDIUM = 3;
const COLUMNS_PER_SCREEN_SMALL = 1;

const getCurrentColumnsCount = () => {
  const windowInnerWidth = window.innerWidth;
  if (windowInnerWidth >= 1200) {
    return COLUMNS_PER_SCREEN_LARGE;
  }
  if (windowInnerWidth >= 640) {
    return COLUMNS_PER_SCREEN_MEDIUM;
  }
  return COLUMNS_PER_SCREEN_SMALL;
};

const FiltersContainer = styled.div`
  height: auto;
  overflow: visible;

  @media screen and (max-width: ${MEDIA_QUERIES.small}) {
    height: ${props => (props.collapsed ? 0 : 'auto')};
    overflow: ${props => (props.collapsed ? 'hidden' : 'visible')};
  }
`;

class ImageSearch extends React.Component {
  UNSAFE_componentWillMount() {
    const { params } = this.props.match;
    const props = iterUnpack(JSON.parse(window.props));

    const isPopularImagesRoute = params.categorySlug === POPULAR_IMAGES.slug;
    // Popular images route combine category slug and search term in the URL
    // ../popular-images/<filters> instead of ../<category-slug>/<search-term>/<filters>
    const { filters, sort } = this.getFiltersFromPath(
      isPopularImagesRoute ? `${params.search}/${params.filters}` : params.filters,
    );

    this.userId = props.userId;
    this.segment = new SegmentClient();

    const initialLoadLimit = getCurrentColumnsCount() * ROWS_PER_LOAD;
    props.images = props.images.slice(0, initialLoadLimit);
    props.metadata.allImages.endCursor = props.images.length;

    this.setState(Object.assign(
      this.defaultState(),
      props,
      { searchTerms: prepareOptions(props.searchTerms) },
      { types: prepareOptions(props.types) },
      { allTypes: invert({ ...props.allTypes }) },
      { materials: prepareOptions(props.materials) },
      { styles: prepareOptions(props.styles) },
      { environments: prepareOptions(props.environments) },
      { locations: prepareOptions(props.locations) },
      { firms: prepareOptions(props.firms) },
      { isLoading: false },
      { selectedSearchTerm: this.prepareSelectedSearchTermOption() },
      { categorySlug: params.categorySlug },
      { filters },
      { selectedSort: sort },
    ), () => {
      this.updateDocumentTitle();
      // Load new images if filters are passed through URL
      if (this.numberOfFiltersAndSorting()) {
        this.getImages(OPERATION.LOAD_NEW);
      }
      // Wrapped in a timeout to wait for Segment client to be fully loaded
      setTimeout(() => {
        this.sendAnalytics(ANALYTICS_NAME.SEARCH_SUBMITTED);
      }, 0);
    });

    userResource.me()
      .then(user => this.setState({ user: user.data }))
      .catch(() => {})
      .finally(() => this.setState({
        canSaveImage: userResource.canSaveImage(),
      }));
  }

  componentDidMount() {
    this.historyUnlistener = this.props.history.listen(architizerHistoryListener((
      location,
      action,
    ) => {
      if (action === 'POP') {
        const { pathname, key } = location;
        const { params } = this.props.match;
        const isImageSearchUrl = new RegExp(BASE_URL_PATTERN).test(pathname);

        if (isImageSearchUrl && key !== this.state.locationKey) {
          const isPopularImagesRoute = params.categorySlug === POPULAR_IMAGES.slug;
          const { filters, sort } = this.getFiltersFromPath(
            isPopularImagesRoute
              ? `${params.search}/${params.filters}`
              : params.filters,
          );
          this.setState({
            filters,
            selectedSort: sort,
          }, () => this.getImages(OPERATION.LOAD_NEW, false));
        }
      }
    }));
    if (this.state.images.length < 1) {
      this.getImages(OPERATION.LOAD_NEW, false);
    }
  }

  componentWillUnmount() {
    if (this.historyUnlistener) {
      this.historyUnlistener();
    }
  }

  onSaveImageClick = (image) => {
    const { canSaveImage } = this.state;

    if (!canSaveImage) {
      this.setState({
        openRequireRegister: true,
        permissionType: PERMISSION_TYPES.SAVE_IMAGE,
        requireRegisterNext: `/image-download/${image.mediaAttributionId}`,
        destinationTitle: PAGES_TITLES.SAVE_IMAGE,
      });
    }
  }

  onFilterChange = (value, filter) => this.setState(Object.assign(
    this.state.filters,
    { [filter]: value },
  ), () => {
    this.getImages(OPERATION.LOAD_NEW);
    this.updateDocumentTitle();
  });

  getInitFilters = (overrideFilters = {}) => {
    const filters = {};
    Object.values(filtersMap).forEach((v) => { filters[v] = []; });
    return Object.assign({}, filters, overrideFilters);
  };

  getFiltersFromPath = (path) => {
    // Split by '/' to get all filters
    const filters = {};
    let sort = SORTINGS[0];
    const params = (path || '').split('/');
    params.forEach((param) => {
      // Split by '=' to get filter name and values
      const filterStr = param.split('=');
      // Sorting
      if (filterStr[0] === SORTING_KEY) {
        const decodedValue = decodeURIComponent(filterStr[1]);
        if (decodedValue === SORTINGS[1].value) {
          sort = SORTINGS[1];
        } else if (decodedValue === SORTINGS[2].value) {
          sort = SORTINGS[2];
        }
      // Filters
      } else {
        const name = filtersMap[filterStr[0]];
        // Split by ',' to get filter values
        const values = (filterStr[1] || '').split(',');
        if (name && values.length > 0) {
          filters[name] = values
            .map(v => decodeURIComponent(v))
            .filter(v => v)
            .map(v => prepareOption(v));
        }
      }
    });

    return {
      sort,
      filters: this.getInitFilters(filters),
    };
  };

  getImages = (opType = OPERATION.LOAD_MORE, saveFilters = true) => {
    if (this.state.isLoading) {
      return;
    }

    this.setState({ isLoading: true, error: null });

    const {
      selectedSearchTerm,
      selectedSort,
      filters: {
        selectedTypes,
        selectedMaterials,
        selectedStyles,
        selectedEnvironments,
        selectedLocations,
        selectedFirms,
      },
    } = this.state;
    const endCursor = opType === OPERATION.LOAD_NEW ? '0' : this.state.metadata.allImages.endCursor;
    let { randomSeed } = this.state;
    const { randomSeedsSet } = this.state;
    let filters = '';
    let pageSize = getCurrentColumnsCount() * ROWS_PER_LOAD;

    if (opType === OPERATION.LOAD_NEW) {
      randomSeed = getRandomSeed(randomSeedsSet);
      this.setState({ images: [], randomSeed });
      filters = `
      types
      materials
      styles
      environments
      locations
      firms`;

      setTimeout(() => {
        this.sendAnalytics(ANALYTICS_NAME.SEARCH_SUBMITTED);
      }, 0);
    } else if (opType === OPERATION.LOAD_MORE) {
      const countColumns = pageSize / ROWS_PER_LOAD;
      const extraTilesAfterResize = this.state.images.length % countColumns;
      if (extraTilesAfterResize > 0) {
        // To fill gaps
        const countExtraImages = Math.max(countColumns - extraTilesAfterResize, 0);
        pageSize += countExtraImages;
      }
    }
    if (saveFilters) {
      if (this.isPopularImagesSelected()) {
        this.pushHistory(URL_TYPE.POPULAR_IMAGES, this.buildFiltersPath());
      } else {
        this.pushHistory(URL_TYPE.CLASSIFICATION, this.buildFiltersPath());
      }
    }

    const query = `{
      allImages(
        first: ${pageSize},
        after: "${endCursor}",
        searchTerm: ${prepareOptionAsGqlParameter(selectedSearchTerm)},
        types: ${prepareOptionsAsGqlParameter(selectedTypes)},
        materials: ${prepareOptionsAsGqlParameter(selectedMaterials)},
        styles: ${prepareOptionsAsGqlParameter(selectedStyles)},
        environments: ${prepareOptionsAsGqlParameter(selectedEnvironments)},
        locations: ${prepareOptionsAsGqlParameter(selectedLocations)},
        firms: ${prepareOptionsAsGqlParameter(selectedFirms)},
        sort: ${prepareOptionAsGqlParameter(selectedSort)},
        ${randomSeed ? `randomSeed: ${randomSeed},` : ''}
        ) {
        images: edges {
          cursor
          node {
            globalId
            mediaId
            mediaAttributionId
            url
            projectUrl
            projectName
            firmNames
            firmIds
            firmSlugs
          }
        }
        totalCount
        pageInfo {
          hasNextPage
          endCursor
        }
        ${filters}
      }
    }`;
    APIClient(query, true)
      .then((data) => {
        const images = [
          ...(opType === OPERATION.LOAD_MORE ? this.state.images : []),
          ...(data.allImages.images.map(e => e.node)),
        ];
        const updatedState = {
          metadata: data.metadata,
          totalCount: data.allImages.totalCount,
          isLoading: false,
          images,
          error: null,
        };
        if (opType === OPERATION.LOAD_NEW) {
          updatedState.types = prepareOptions(data.allImages.types);
          updatedState.materials = prepareOptions(data.allImages.materials);
          updatedState.styles = prepareOptions(data.allImages.styles);
          updatedState.environments = prepareOptions(data.allImages.environments);
          updatedState.locations = prepareOptions(data.allImages.locations);
          updatedState.firms = prepareOptions(data.allImages.firms);
        }
        this.setState(updatedState);
      })
      .catch(() => {
        // console.error('Error fetching images:', error);
        this.setState({
          isLoading: false,
          error: 'There was an error fetching the images. Our engineers are handling it.',
        });
      });
  };

  getFilterOptions = (input, callback, field = '') => {
    const {
      selectedSearchTerm, filters: {
        selectedTypes, selectedMaterials,
        selectedStyles, selectedEnvironments, selectedLocations, selectedFirms,
      },
      randomSeed,
    } = this.state;

    const query = `{
      allImages(
        first: 0,
        input: "${encodeURIComponent(input)}",
        searchTerm: ${prepareOptionAsGqlParameter(selectedSearchTerm)},
        types: ${prepareOptionsAsGqlParameter(selectedTypes)},
        materials: ${prepareOptionsAsGqlParameter(selectedMaterials)},
        styles: ${prepareOptionsAsGqlParameter(selectedStyles)},
        environments: ${prepareOptionsAsGqlParameter(selectedEnvironments)},
        locations: ${prepareOptionsAsGqlParameter(selectedLocations)},
        firms: ${prepareOptionsAsGqlParameter(selectedFirms)},
        ${randomSeed ? `randomSeed: ${randomSeed},` : ''}
      ) {
        ${field}
      }
    }`;
    return APIClient(query, true)
      .then(data => prepareOptions(data.allImages[field]))
      .then(options => callback(null, { options }));
  };

  getProjectSlugFromUrl = (url) => {
    if (!url) {
      return false;
    }
    const result = url.match('/projects/([0-9a-z-_]+)/');
    if (result && result.length >= 1) {
      return result[1];
    }
    return false;
  };

  pushHistory = (type = URL_TYPE.CLASSIFICATION, suffix = '') => {
    const { categorySlug, selectedSearchTerm } = this.state;
    let href = '';

    switch (type) {
      case (URL_TYPE.DETAIL):
        href = `/${BASE_URL}/${DETAIL_CATEGORY}/${suffix}`;
        break;
      case (URL_TYPE.POPULAR_IMAGES):
        href = `/${BASE_URL}/${POPULAR_IMAGES.slug}/${suffix}`;
        break;
      default:
        href = `/${BASE_URL}/${categorySlug}/${selectedSearchTerm ? selectedSearchTerm.value : ''}/${suffix}`;
    }

    this.props.history.push(href, {
      previousPage: {
        title: document.title,
        url: window.location.href,
      },
    });
    this.segment.page();
    ga4.page();
  };

  numberOfFiltersAndSorting = () => {
    const filtersCount = Object.keys(this.state.filters)
      .reduce((acc, k) => acc + this.state.filters[k].length, 0);
    const sortingCount = isEqual(this.state.selectedSort, SORTINGS[0]) ? 0 : 1;
    return filtersCount + sortingCount;
  }

  buildFiltersPath = () => [
    // Sorting
    ...(isEqual(this.state.selectedSort, SORTINGS[0])
      ? []
      : [`${SORTING_KEY}=${this.state.selectedSort.value}`]),
    // Filters
    ...Object.keys(this.state.filters).map((key) => {
      const fi = this.state.filters;
      const values = fi[key].map(f => encodeURIComponent(f.value)).join(',');
      return values && `${invert(filtersMap)[key]}=${values}`;
    }).filter(e => e),
  ].join('/');

  prepareSelectedSearchTermOption = () => {
    const { params } = this.props.match;
    let selectedSearchTerm = '';
    if (params.categorySlug === POPULAR_IMAGES.slug) {
      selectedSearchTerm = POPULAR_IMAGES.text;
    } else if (params.categorySlug === 'idea') {
      selectedSearchTerm = POPULAR_IMAGES.text;
    } else {
      selectedSearchTerm = decodeURIComponent(params.search) || '';
    }
    return prepareOption(selectedSearchTerm);
  };

  defaultState = () => ({
    metadata: {
      allImages: {
        endCursor: '0',
        hasNextPage: false,
      },
    },
    totalCount: 0,
    isSuperuser: false,
    userId: null,
    isLoading: true,
    images: [],
    searchTerms: [],
    allTypes: {},
    types: [],
    materials: [],
    styles: [],
    environments: [],
    locations: [],
    firms: [],
    selectedSearchTerm: null,
    filters: this.getInitFilters(),
    randomSeed: null,
    randomSeedsSet: [],
    isFiltersCollapsed: true,
    selectedSort: SORTINGS[0],
    locationKey: null,
    user: null,
    openRequireRegister: false,
    requireRegisterNext: null,
    canSaveImage: false,
    permissionType: null,
    destinationTitle: null,
    destinationUrl: null,
  });

  selectPlaceholderAsValue = label => <div className="Select-placeholder forced-placeholder">{label}</div>;

  segment = {};

  removeFilter = (category, filter) => {
    if (this.state.isLoading) {
      return;
    }
    this.setState(Object.assign(
      this.state.filters,
      { [category]: this.state.filters[category].filter(e => e.value !== filter.value) },
    ), () => {
      this.getImages(OPERATION.LOAD_NEW);
      this.updateDocumentTitle();
    });
  };

  /**
   * Use it to clear all filters, but keeping Search Term as is.
   * @param {Array} overrideFilters - array of filters that will be selected after the clear.
   * Returns undefined.
   */
  clearFilters = (overrideFilters = {}) => {
    if (this.state.isLoading) {
      return;
    }

    const isPopularImagesSelected = this.isPopularImagesSelected();
    this.setState({
      filters: this.getInitFilters(overrideFilters),
      categorySlug: isPopularImagesSelected ? POPULAR_IMAGES.slug : 'building-features',
      selectedSort: SORTINGS[0],
    }, () => {
      this.updateDocumentTitle();
      this.getImages(OPERATION.LOAD_NEW);
    });
  };

  /**
   * Use it to clear all filters and reset Search Term.
   * @param {Array} overrideFilters - array of filters that will be selected after the clear.
   * Returns undefined.
   */
  resetAll = (overrideFilters = {}) => {
    if (this.state.isLoading) {
      return;
    }

    this.setState({
      selectedSearchTerm: this.state.searchTerms[0],
      filters: this.getInitFilters(overrideFilters),
      selectedSort: SORTINGS[0],
    }, () => {
      this.updateDocumentTitle();
      this.getImages(OPERATION.LOAD_NEW);
    });
  }

  updateDocumentTitle = () => {
    const {
      selectedSearchTerm, filters: {
        selectedStyles, selectedMaterials,
        selectedTypes, selectedLocations,
      },
    } = this.state;
    document.title = `
      Most Popular
      ${arrayToReadableString(selectedStyles, { textField: 'value', suffix: ' ', serialComma: true })}
      ${arrayToReadableString(selectedMaterials, { textField: 'value', suffix: ' ', serialComma: true })}
      ${arrayToReadableString(selectedTypes, { textField: 'value', suffix: ' ', serialComma: true })}
      ${this.isPopularImagesSelected() ? '' : selectedSearchTerm.value}
      Ideas
      ${arrayToReadableString(selectedLocations, { textField: 'value', prefix: 'in ', serialComma: true })}
      - Architizer`;
  }

  isPopularImagesSelected = () => (
    this.state.selectedSearchTerm && this.state.selectedSearchTerm.value === POPULAR_IMAGES.text
  );

  handleSearchTermsChange = (option) => {
    const optionFromProjectType = this.state.allTypes[option.label];
    this.setState({
      // selectedSearchTerm is set to "Popular Images" when option is a project type
      // otherwise, it is set to the selected option
      selectedSearchTerm: optionFromProjectType ? this.state.searchTerms[0] : option,
    }, () => {
      // When option is a project type, selectedTypes is set to have option
      this.clearFilters({
        selectedTypes: optionFromProjectType ? [option] : [],
      });
    });
  };

  selectPopularImages = () => this.handleSearchTermsChange(this.state.searchTerms[0]);

  sendAnalytics = (name, props = {}) => {
    const { selectedSearchTerm, filters } = this.state;
    const parsedFilters = {};
    const selectedFilters = pickBy(filters, v => v && v.length);
    Object.keys(selectedFilters).forEach((k) => {
      parsedFilters[invert(filtersMap)[k]] = selectedFilters[k].map(v => v.value).join(',');
    });

    this.segment.track(
      name,
      Object.assign(
        {
          userId: this.userId,
        },
        pickBy({ search_term: selectedSearchTerm && selectedSearchTerm.value }),
        parsedFilters,
        { ...props },
      ),
    );
  };

  handleResultClick = (image) => {
    // location.key is used to decide wheather to make a new
    // request to get images on history POP or not, in other words,
    // when coming from universal modal, do not make any requests
    this.setState({ locationKey: this.props.history.location.key });

    const { selectedSearchTerm, filters } = this.state;
    const parsedFilters = {};
    const selectedFilters = pickBy(filters, v => v && v.length);
    Object.keys(selectedFilters).forEach((k) => {
      parsedFilters[invert(filtersMap)[k]] = selectedFilters[k].map(v => v.value).join(',');
    });
    try {
      window.openMediaUniversalModal({
        ids: this.state.images.map(img => img.mediaAttributionId),
        activeId: image.mediaAttributionId,
        callSource: 'IMAGE_SEARCH',
        extraAnalyticsInfo: {
          source: 'image serach',
          ...pickBy({ search_term: selectedSearchTerm && selectedSearchTerm.value }),
          ...parsedFilters,
        },
      });
      this.sendAnalytics(ANALYTICS_NAME.RESULT_CLICKED, {
        source: 'image_modal',
        url: `/idea/${image.mediaAttributionId}`,
      });
    } catch (err) {
      Raven.captureException(err);
    }
  }

  filterByFirmName = firmName => this.resetAll({ [filtersMap.firm]: [prepareOption(firmName)] });

  numberOfSelectedFilters = () => Object.keys(this.state.filters)
    .map(k => this.state.filters[k].length || 0)
    .reduce((acc, v) => v + acc, 0);

  render() {
    const {
      isSuperuser,
      totalCount,
      isLoading,
      images,
      searchTerms,
      types,
      materials,
      styles,
      environments,
      locations,
      firms,
      selectedSearchTerm,
      selectedSort,
      categorySlug,
      filters,
      canSaveImage,
      permissionType,
      destinationTitle,
      destinationUrl,
      filters: {
        selectedTypes,
        selectedMaterials,
        selectedStyles,
        selectedEnvironments,
        selectedLocations,
        selectedFirms,
      },
      isFiltersCollapsed,
      error,
    } = this.state;

    const breadcrumbs = [
      ['All Categories', `/${BASE_URL}`],
      ...CATEGORIES.INVERTED_SLUG[categorySlug]
        ? [[CATEGORIES.TEXT[CATEGORIES.INVERTED_SLUG[categorySlug]], `/${BASE_URL}/${categorySlug}`]]
        : [],
      [selectedSearchTerm.value, null],
    ];
    const isPopularImagesSelected = this.isPopularImagesSelected();

    return (
      <>
        {/* Search */}
        <div className="row bg-black">
          <div className="column gutter-xxl small-gutter-xs">
            <div className="row align-middle">
              {/* Engines Dropdown */}
              <div className="columns mt-base mb-base shrink show-for-medium">
                {/* TODO: Replace with react-select after upgrading to v2 */}
                <ul className="dropdown menu" data-dropdown-menu data-click-open data-disable-hover>
                  <li>
                    <h4 className="white cursor-pointer">
                      Images
                      <i className="material-icons ml-xxs">keyboard_arrow_down</i>
                    </h4>
                    <ul>
                      <li><span className="p-base cursor-pointer">Images</span></li>
                    </ul>
                  </li>
                </ul>
              </div>
              {/* Search Term */}
              <div className="columns mt-base mb-base small-12 medium-6 large-6 xlarge-3">
                <Select
                  name="image-search-term-selector"
                  className="large border-gray-200 br-s cursor-after-placeholder"
                  placeholder={(
                    <span>
                      <i className="material-icons mr-xxs">search</i>
                      Search Building Space or Component
                    </span>
)}
                  onChange={this.handleSearchTermsChange}
                  value={selectedSearchTerm}
                  options={searchTerms}
                  disabled={isLoading}
                  tabSelectsValue={false}
                  deleteRemoves={false}
                  backspaceRemoves={false}
                  resetValue={searchTerms[0]}
                  ignoreAccents={false}
                />
              </div>
              {/* Submit - medium and up */}
              <div className="columns mt-base mb-base shrink show-for-medium">
                <button
                  className="button p-s"
                  onClick={() => this.getImages(OPERATION.LOAD_NEW)}
                  disabled={isLoading}
                  type="button"
                >
                  Search
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Heading */}
        <div className="row bg-extra-light-gray">
          <div className="columns gutter-xxl small-gutter-xs mt-base mb-base">
            <div className="fs-s">
              {breadcrumbs.map((breadcrumb, i) => (
                <Fragment key={breadcrumb[0]}>
                  <a href={breadcrumb[1]} className="black">{breadcrumb[0]}</a>
                  {i + 1 < breadcrumbs.length && <i className="material-icons">keyboard_arrow_right</i>}
                </Fragment>
              ))}
            </div>
            <h1 className="fs-l" style={{ textTransform: 'capitalize' }}>
              {`
                ${!selectedTypes.length && !isPopularImagesSelected ? 'All ' : ''}
                ${isPopularImagesSelected && (selectedMaterials.length || selectedStyles.length) ? 'Popular ' : ''}
                ${arrayToReadableString(selectedMaterials, { textField: 'value', suffix: ' ', serialComma: true })}
                ${arrayToReadableString(selectedStyles, { textField: 'value', suffix: ' ', serialComma: true })}
                ${arrayToReadableString(selectedTypes, {
                textField: 'value', suffix: ' ', prefix: isPopularImagesSelected && !selectedMaterials.length && !selectedStyles.length ? 'Popular ' : '', serialComma: true,
              })}
                ${selectedSearchTerm && !isPopularImagesSelected ? selectedSearchTerm.value : ''}
                ${isPopularImagesSelected && !selectedMaterials.length && !selectedStyles.length && !selectedTypes.length ? 'Popular Image' : ''}
                Ideas
                ${arrayToReadableString(selectedLocations, { textField: 'value', prefix: 'in ', serialComma: true })}
              `}
            </h1>
          </div>
        </div>

        {/* Filters */}
        <div className="row bg-extra-light-gray" style={{ boxShadow: 'rgba(231, 237, 239, .85) 0px 2px 2px 0px' }}>
          <div className="column gutter-xxl small-gutter-xs">
            {/* Mobile Trigger */}
            <div className="row">
              <div className="columns mb-base small-12 show-for-small-only">
                <button
                  className="button p-s"
                  style={{ width: '100%' }}
                  onClick={() => this.setState({
                    isFiltersCollapsed: !isFiltersCollapsed,
                  })}
                  type="button"
                >
                  <i className="material-icons mr-xxs">{isFiltersCollapsed ? 'arrow_downward' : 'arrow_upward'}</i>
                  {isFiltersCollapsed ? 'Show Filters' : 'Hide Filters'}
                </button>
              </div>
            </div>

            {/* Comboboxes */}
            <FiltersContainer className="row" collapsed={isFiltersCollapsed}>
              {/* Type */}
              <div className="columns mb-base small-12 medium-6 large-2">
                <Select
                  name="project-type-selector"
                  className="large border-gray-200 br-s highlight-on-select"
                  placeholder="Project Type"
                  onChange={value => this.onFilterChange(value, filtersMap.type)}
                  value={selectedTypes}
                  options={types}
                  multi
                  valueComponent={() => this.selectPlaceholderAsValue('Project Type')}
                  tabSelectsValue={false}
                  noResultsText={emptyFilterMessage}
                  ignoreAccents={false}
                  disabled={isLoading}
                />
              </div>

              {/* Mobile Divider */}
              <div className="columns small-12 show-for-small-only">
                <hr className="mt-0 mb-xs border-global-separator" />
              </div>

              {/* Material */}
              <div className="columns mb-base small-12 medium-6 large-2 xlarge-1">
                <Select
                  name="project-material-selector"
                  className="large border-gray-200 br-s highlight-on-select"
                  placeholder="Material"
                  onChange={value => this.onFilterChange(value, filtersMap.material)}
                  value={selectedMaterials}
                  options={materials}
                  multi
                  valueComponent={() => this.selectPlaceholderAsValue('Material')}
                  tabSelectsValue={false}
                  noResultsText={emptyFilterMessage}
                  ignoreAccents={false}
                  disabled={isLoading}
                />
              </div>
              {/* Style */}
              <div className="columns mb-base small-12 medium-6 large-2 xlarge-1">
                <Select
                  name="project-style-selector"
                  className="large border-gray-200 br-s highlight-on-select"
                  placeholder="Style"
                  onChange={value => this.onFilterChange(value, filtersMap.style)}
                  value={selectedStyles}
                  options={styles}
                  multi
                  valueComponent={() => this.selectPlaceholderAsValue('Style')}
                  tabSelectsValue={false}
                  noResultsText={emptyFilterMessage}
                  ignoreAccents={false}
                  disabled={isLoading}
                />
              </div>
              {/* Environments */}
              <div className="columns mb-base small-12 medium-6 large-2 xlarge-1">
                <Select
                  name="project-environment-selector"
                  className="large border-gray-200 br-s highlight-on-select"
                  placeholder="Environment"
                  onChange={value => this.onFilterChange(value, filtersMap.environment)}
                  value={selectedEnvironments}
                  options={environments}
                  multi
                  valueComponent={() => this.selectPlaceholderAsValue('Environment')}
                  tabSelectsValue={false}
                  noResultsText={emptyFilterMessage}
                  ignoreAccents={false}
                  disabled={isLoading}
                />
              </div>

              {/* Mobile Divider */}
              <div className="columns small-12 show-for-small-only">
                <hr className="mt-0 mb-xs border-global-separator" />
              </div>

              {/* Location */}
              <div className="columns mb-base small-12 medium-6 large-2">
                <Select.Async
                  name="project-location-selector"
                  className="large border-gray-200 br-s highlight-on-select"
                  placeholder="Location"
                  onChange={value => this.onFilterChange(value, filtersMap.location)}
                  value={selectedLocations}
                  loadOptions={(input, callback) => this.getFilterOptions(input, callback, 'locations')}
                  autoload={false}
                  cache={false}
                  options={locations}
                  multi
                  valueComponent={() => this.selectPlaceholderAsValue('Location')}
                  tabSelectsValue={false}
                  noResultsText={emptyFilterMessage}
                  ignoreAccents={false}
                  disabled={isLoading}
                />
              </div>
              {/* Firm */}
              <div className="columns mb-base small-12 medium-6 large-2">
                <Select.Async
                  name="project-firm-selector"
                  className="large border-gray-200 br-s highlight-on-select"
                  placeholder="Firm"
                  onChange={value => this.onFilterChange(value, filtersMap.firm)}
                  value={selectedFirms}
                  loadOptions={(input, callback) => this.getFilterOptions(input, callback, 'firms')}
                  autoload={false}
                  cache={false}
                  options={firms}
                  multi
                  valueComponent={() => this.selectPlaceholderAsValue('Firm')}
                  tabSelectsValue={false}
                  noResultsText={emptyFilterMessage}
                  ignoreAccents={false}
                  disabled={isLoading}
                />
              </div>
            </FiltersContainer>
          </div>
        </div>
        {/* Sorting */}
        <div className="row mt-s mb-s">
          <div className="columns small-12 medium-4 fs-s gutter-xxl small-gutter-xs medium-text-left">
            {totalCount ? (
              <span>
                Browse
                {' '}
                {numberWithCommas(totalCount)}
                {' '}
                Idea
                {totalCount > 1 ? 's' : ''}
              </span>
            ) : <></>}
          </div>
          <div className="columns small-12 medium-8 fs-s gutter-xxl small-gutter-xs medium-text-right">
            <span>Sort By: </span>
            {SORTINGS.map((sorting, i) => (
              <Fragment key={sorting.value}>
                <a
                  className={`black ${isEqual(selectedSort, sorting) ? 'fw-bold' : ''} ${isLoading ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    if (isLoading) {
                      return;
                    }
                    this.setState({
                      selectedSort: sorting,
                    }, () => this.getImages(OPERATION.LOAD_NEW));
                  }}
                >
                  {sorting.label}
                </a>
                {i + 1 < SORTINGS.length && <span> • </span>}
              </Fragment>
            ))}
          </div>
        </div>
        {/* Selected Filters */}
        {this.numberOfSelectedFilters() > 0 && (
        <div className="row">
          <div className="columns gutter-xxl small-gutter-xs mb-xs">
            {Object.keys(filters).map(key => filters[key].map(filter => (
              <FilterPill
                key={filter.value}
                filter={invert(filtersMap)[key]}
                label={filter.label}
                remove={() => this.removeFilter(key, filter)}
              />
            )))}
            <span
              className={`fs-s medium-gray ${isLoading ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              onClick={() => this.clearFilters()}
              role="button"
              tabIndex={0}
            >
              Clear All
            </span>
          </div>
        </div>
        )}

        {/* Result Set */}
        <div className="row">
          <div className="columns gutter-xxl small-gutter-xs mb-base small-12">
            {error && (
              <div className="error-message" style={{ color: '#e74c3c', marginBottom: '1rem' }}>
                <p>{error}</p>
                <p>Please try again later or contact support if the problem persists.</p>
              </div>
            )}

            <div className="row small-up-1 medium-up-3 large-up-4">
              {images.map(image => (
                <ImageCard
                  key={image.globalId}
                  image={image}
                  canSaveImage={canSaveImage}
                  onSaveImageClick={this.onSaveImageClick}
                  showSettings={isSuperuser}
                  sendAnalytics={this.sendAnalytics}
                  onImageClick={this.handleResultClick}
                  parent={this}
                  openRequireRegisterPopup={requireRegisterNext => this.setState({
                    permissionType: PERMISSION_TYPES.COLLECT,
                    openRequireRegister: true,
                    requireRegisterNext,
                  })}
                  filterByFirmName={this.filterByFirmName}
                />
              ))}
            </div>
            {!images.length && !isLoading && !error
              ? (
                <p className="text-center" style={{ padding: '50px 0 200px 0' }}>
                  No images found, try a different set of search terms.
                </p>
              )
              : <></>}
            {isLoading ? (<div className="loading-spinner-m m-auto" key={0} />) : <></>}
            {this.state.metadata.allImages
            && this.state.metadata.allImages.hasNextPage
            && !isLoading
            && !error
              ? (
                <div className="flex-container align-center">
                  <button
                    type="button"
                    className="button"
                    style={{ margin: '40px auto', padding: '10px 15px' }}
                    onClick={() => this.getImages(OPERATION.LOAD_MORE, false)}
                  >
                    Load More&hellip;
                  </button>
                </div>
              )
              : <></>}
          </div>
        </div>
        {this.state.openRequireRegister && (
          <RequireRegisterPopup
            permissionType={permissionType}
            joinCompany={permissionType === PERMISSION_TYPES.FIND_SIMILAR}
            permissionDestinationTitle={destinationTitle}
            permissionDestinationUrl={destinationUrl}
            submitProfessional
            nextParam={this.state.requireRegisterNext}
            onClose={() => this.setState({
              openRequireRegister: false,
              requireRegisterNext: null,
              destinationTitle: null,
              permissionType: null,
              destinationUrl: null,
            })}
          />
        )}
      </>
    );
  }
}

ImageSearch.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      categorySlug: PropTypes.string,
      search: PropTypes.string,
      filters: PropTypes.string,
    }),
  }).isRequired,
  history: PropTypes.shape({
    location: PropTypes.shape({
      key: PropTypes.string,
    }).isRequired,
    listen: PropTypes.func,
    push: PropTypes.func,
  }).isRequired,
};

ReactDOM.render(
  <BrowserRouter>
    <Route path="/image-search/:categorySlug([a-z-]+)/:search?/:filters(.*)*" component={withRouter(ImageSearch)} />
  </BrowserRouter>,
  document.getElementById('image-search-root'),
);
